import React, { useState, useEffect, useRef } from 'react';
import { Editor, EditorState, RichUtils, ContentState } from 'draft-js';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Quote,
  Strikethrough,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { logDebug } from '@/utils/errorHandler';
import 'draft-js/dist/Draft.css';
import './draft-editor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

// Simple HTML to text conversion for initial content
const htmlToText = (html: string): string => {
  if (!html) return '';
  // Remove HTML tags and decode entities
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

// Simple text to HTML conversion for output
const textToHTML = (editorState: EditorState): string => {
  const contentState = editorState.getCurrentContent();
  const blocks = contentState.getBlocksAsArray();

  let html = '';

  blocks.forEach(block => {
    const text = block.getText();
    const type = block.getType();
    const inlineStyles = block.getInlineStyleAt(0);

    if (!text.trim() && type === 'unstyled') {
      html += '<p><br></p>';
      return;
    }

    let formattedText = text;

    // Apply inline styles (simplified approach)
    if (inlineStyles.has('BOLD')) {
      formattedText = `<strong>${formattedText}</strong>`;
    }
    if (inlineStyles.has('ITALIC')) {
      formattedText = `<em>${formattedText}</em>`;
    }
    if (inlineStyles.has('UNDERLINE')) {
      formattedText = `<u>${formattedText}</u>`;
    }
    if (inlineStyles.has('STRIKETHROUGH')) {
      formattedText = `<s>${formattedText}</s>`;
    }
    if (inlineStyles.has('CODE')) {
      formattedText = `<code>${formattedText}</code>`;
    }

    // Apply block styles
    switch (type) {
      case 'header-one':
        html += `<h1>${formattedText}</h1>`;
        break;
      case 'header-two':
        html += `<h2>${formattedText}</h2>`;
        break;
      case 'header-three':
        html += `<h3>${formattedText}</h3>`;
        break;
      case 'blockquote':
        html += `<blockquote>${formattedText}</blockquote>`;
        break;
      case 'code-block':
        html += `<pre><code>${text}</code></pre>`;
        break;
      case 'unordered-list-item':
        html += `<ul><li>${formattedText}</li></ul>`;
        break;
      case 'ordered-list-item':
        html += `<ol><li>${formattedText}</li></ol>`;
        break;
      default:
        html += `<p>${formattedText}</p>`;
    }
  });

  return html;
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text here...',
  className,
}) => {
  const [editorState, setEditorState] = useState(() => {
    if (value) {
      try {
        // Create content from plain text (simplified approach)
        const text = htmlToText(value);
        const contentState = ContentState.createFromText(text);
        return EditorState.createWithContent(contentState);
      } catch {
        logDebug('Rich text editor content parsing failed', {
          context: 'RichTextEditor.initialization',
          value: value,
          fallbackAction: 'using empty state',
          category: 'content_parsing',
        });
        return EditorState.createEmpty();
      }
    }
    return EditorState.createEmpty();
  });

  const editorRef = useRef<Editor>(null);
  const isUserTyping = useRef(false);

  // Convert editor state to HTML and call onChange
  const handleEditorChange = (newEditorState: EditorState) => {
    isUserTyping.current = true;
    setEditorState(newEditorState);
    const html = textToHTML(newEditorState);
    onChange(html);
    // Reset the flag after a brief delay to allow external updates
    setTimeout(() => {
      isUserTyping.current = false;
    }, 100);
  };

  // Keep track of the last external value to prevent unnecessary updates
  const lastExternalValue = useRef(value);

  // Update editor state when value prop changes (only from external sources)
  useEffect(() => {
    // Don't sync if user is actively typing or if value hasn't actually changed
    if (isUserTyping.current || value === lastExternalValue.current) return;

    const newText = htmlToText(value || '');
    lastExternalValue.current = value;

    if (newText !== '') {
      try {
        const contentState = ContentState.createFromText(newText);
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
      } catch {
        logDebug('Rich text editor value update failed', {
          context: 'RichTextEditor.useEffect',
          newText: newText,
          fallbackAction: 'keeping current state',
          category: 'editor_sync',
        });
      }
    }
  }, [value]);

  // Handle keyboard commands
  const handleKeyCommand = (command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  // Handle Enter key behavior
  const handleReturn = (e: React.KeyboardEvent) => {
    // Shift+Enter creates a soft line break within the same block
    if (e.shiftKey) {
      handleEditorChange(RichUtils.insertSoftNewline(editorState));
      return 'handled';
    }

    // Regular Enter creates a new block - let Draft.js handle this naturally
    return 'not-handled';
  };

  // Handle formatting commands
  const handleInlineStyle = (style: string) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const handleBlockType = (blockType: string) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  // Get current styles and block type
  const currentStyle = editorState.getCurrentInlineStyle();
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  // Check if a style is active
  const isStyleActive = (style: string) => currentStyle.has(style);
  const isBlockActive = (type: string) => blockType === type;

  // Focus editor
  const focusEditor = () => {
    editorRef.current?.focus();
  };

  return (
    <div className={cn('border rounded-md overflow-hidden bg-white', className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b bg-slate-50 p-2 dark:bg-slate-800">
        <TooltipProvider>
          {/* Text Formatting */}
          <div className="mr-2 flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isStyleActive('BOLD') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleInlineStyle('BOLD')}
                  className="size-8 p-0"
                >
                  <Bold className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Bold (Ctrl+B)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isStyleActive('ITALIC') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleInlineStyle('ITALIC')}
                  className="size-8 p-0"
                >
                  <Italic className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Italic (Ctrl+I)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isStyleActive('UNDERLINE') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleInlineStyle('UNDERLINE')}
                  className="size-8 p-0"
                >
                  <Underline className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Underline (Ctrl+U)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isStyleActive('STRIKETHROUGH') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleInlineStyle('STRIKETHROUGH')}
                  className="size-8 p-0"
                >
                  <Strikethrough className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Strikethrough</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isStyleActive('CODE') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleInlineStyle('CODE')}
                  className="size-8 p-0"
                >
                  <Code className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Inline Code</TooltipContent>
            </Tooltip>
          </div>

          {/* Divider */}
          <div className="mr-2 h-6 w-px bg-slate-300 dark:bg-slate-600" />

          {/* Headings */}
          <div className="mr-2 flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isBlockActive('header-one') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleBlockType('header-one')}
                  className="size-8 p-0"
                >
                  <Heading1 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Heading 1</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isBlockActive('header-two') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleBlockType('header-two')}
                  className="size-8 p-0"
                >
                  <Heading2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Heading 2</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isBlockActive('header-three') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleBlockType('header-three')}
                  className="size-8 p-0"
                >
                  <Heading3 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Heading 3</TooltipContent>
            </Tooltip>
          </div>

          {/* Divider */}
          <div className="mr-2 h-6 w-px bg-slate-300 dark:bg-slate-600" />

          {/* Lists */}
          <div className="mr-2 flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isBlockActive('unordered-list-item') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleBlockType('unordered-list-item')}
                  className="size-8 p-0"
                >
                  <List className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Bullet List</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isBlockActive('ordered-list-item') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleBlockType('ordered-list-item')}
                  className="size-8 p-0"
                >
                  <ListOrdered className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Numbered List</TooltipContent>
            </Tooltip>
          </div>

          {/* Divider */}
          <div className="mr-2 h-6 w-px bg-slate-300 dark:bg-slate-600" />

          {/* Block Formatting */}
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={isBlockActive('blockquote') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleBlockType('blockquote')}
                  className="size-8 p-0"
                >
                  <Quote className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Quote</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {/* Editor */}
      <div
        className="prose min-h-[200px] max-w-none cursor-text p-4 dark:prose-invert"
        onClick={focusEditor}
      >
        <Editor
          ref={editorRef}
          editorState={editorState}
          onChange={handleEditorChange}
          handleKeyCommand={handleKeyCommand}
          handleReturn={handleReturn}
          placeholder={placeholder}
          spellCheck={true}
        />
      </div>
    </div>
  );
};
